// GenCC validity grades for the genes on screen (TGCLOUD-1794, Phase 3).
//
// Fetched CLIENT-SIDE, deliberately. The obvious alternative — asking
// /profile-search to attach grades — would mean sending the patient's genes to
// the terminology service, and Clinical Insights ranking is driven by phenotype
// alone. Suggestions.test.jsx pins that boundary: no gene symbol or gene id may
// appear in a profile-search request, even with the molecular boost switched on.
// The annotation is an overlay on the ranking, so it is fetched the same way the
// ranking's other overlays are.
//
// One batched request for every gene in view, through the endpoint Phase 2
// already built. The response is passed through UNMODIFIED, including
// `available: false`: flattening a failed lookup to an empty map here would
// erase the difference between "GenCC has no submission for this gene" and "we
// could not ask", and the second rendered as the first is a clinical claim made
// on the strength of a network timeout.

import { useEffect, useRef, useState } from 'react';
import axios from 'src/axios';
import { APICONSTANTS } from 'src/components/Constants';

const EMPTY = Object.freeze({
  available: false,
  byGene: {},
  datasetVersion: null
});

export default function useGenccGrades(geneSymbols, { enabled = true } = {}) {
  const [state, setState] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  // Stable key so a re-render with the same genes does not refetch.
  const genes = Array.isArray(geneSymbols)
    ? [
        ...new Set(
          geneSymbols.map((g) => String(g || '').trim()).filter(Boolean)
        )
      ].sort()
    : [];
  const key = genes.join('|');
  const requestedFor = useRef(null);

  useEffect(() => {
    if (!enabled || !genes.length) {
      setState(EMPTY);
      setLoading(false);
      requestedFor.current = null;
      return undefined;
    }

    let cancelled = false;
    requestedFor.current = key;
    setLoading(true);

    (async () => {
      try {
        const { data } = await axios.post(APICONSTANTS.genccByGenes, { genes });
        if (cancelled || requestedFor.current !== key) return;
        setState({
          available: data?.available === true,
          byGene: data?.byGene || {},
          datasetVersion: data?.datasetVersion ?? null,
          reason: data?.reason ?? null
        });
      } catch (err) {
        if (cancelled || requestedFor.current !== key) return;
        // A thrown request is an unavailable answer, never an empty one.
        setState({ ...EMPTY, reason: 'unreachable' });
      } finally {
        if (!cancelled && requestedFor.current === key) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
    // `key` is the content of `genes`; depending on the array itself would
    // refetch on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, enabled]);

  return { ...state, loading };
}
